import { styled } from '@styledComponents';
import { Box } from '@components/Box';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { animationTransition, animationTransitionExt, STANDARD_DURATION } from '@utils/animationTransition';
import { CloudImage } from '@components/CloudImage';
import { Text } from '@components/typography';
import { Avatar } from '@components/Avatar';
import { Columns } from '@components/Columns';
import { TextPairing } from '@components/TextPairing';
import { PhotoCredit } from '@components/PhotoCredit';

export const SectionBox = styled(Box)`
  position: relative;
`;

export const SecondSectionBox = styled(Box)`
  position: relative;
  height: 1800px;
  z-index: -1;

  ${themeMediaQuery('below_sm')} {
    height: 1300px;
  }
`;

export const QuestionBuilderTopBox = styled.div`
  position: relative;
  background-color: #00837b;
  min-height: 627px;
  border-radius: 32px;
  padding: ${themeSpace(10)} ${themeSpace(4)};
  ${themeMediaQuery('sm')} {
    margin: ${themeSpace(-12)} ${themeSpace(-10)} 0 ${themeSpace(-10)};
    padding: ${themeSpace(11)} ${themeSpace(10)};
  }
`;

export const QuestionBuilderColumnTextPairing = styled(TextPairing).attrs({
  iconColor: 'green3',
  titleColor: 'white',
  bodyColor: 'white',
  titleVariant: 'dek24',
  bodyVariant: 'featureCopy17',
  iconSize: [32, 48],
})`
  margin-bottom: ${themeSpace(7)};
`;

export const QuestionsMenu = styled.ul`
  display: flex;
  max-width: 730px;
  justify-content: center;
  margin-top: ${themeSpace(10)};
`;

export const QuestionMenuItem = styled.li<{ isActive?: boolean }>`
  width: 42px;
  height: 42px;
  ${themeMediaQuery('sm')} {
    width: 80px;
    height: 80px;
  }
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  z-index: 0;
  cursor: pointer;
  &:before {
    position: absolute;
    left: 0;
    top: 0;
    content: '';
    background-color: ${({ isActive }) => (isActive ? themeColor('green6') : 'transparent')};
    border-radius: 50%;
    width: 100%;
    height: 100%;
    opacity: 0.2;
    z-index: -1;
    transition: ${animationTransition('background-color')};
  }
  > img {
    opacity: ${({ isActive }) => (isActive ? 1 : 0.4)};
    transition: ${animationTransition('opacity')};
    width: 32px;
    height: 32px;
    ${themeMediaQuery('sm')} {
      width: 49px;
      height: 49px;
    }
  }
  &:hover > img {
    opacity: 1;
  }
  ${themeMediaQuery('below_sm')} {
    pointer-events: none;
  }
`;

export const MultiSectionsContent = styled.div`
  position: relative;
  padding-top: ${themeSpace(8)};
`;
export const SecondSectionTopTextBlock = styled.div`
  position: absolute;
  width: 100%;
  transform: translateY(-115%);
  ${themeMediaQuery('sm')} {
    transform: translateY(-180%);
  }
  opacity: 0;
`;
export const SecondSectionBottomTextBlock = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(130%);
  ${themeMediaQuery('sm')} {
    transform: translateY(220%);
  }
  width: 100%;
  opacity: 0;
`;

export const QuestionCardsContainer = styled.div`
  position: relative;
`;

export const QuestionCardWrapper = styled.div`
  position: relative;
  width: 80vw;
  height: calc(80vw / 1.5);
  min-height: 230px;

  ${themeMediaQuery('sm')} {
    width: 730px;
    height: 468px;
    min-height: 300px;
  }
  margin: auto;
`;

export const QuestionCardShadow = styled.div`
  height: 90%;
  width: 96%;
  box-shadow: 0px 32px 64px rgba(255, 242, 227, 0.5);
  border-radius: 32px;
  position: absolute;
  left: 2%;
  top: 10%;
  z-index: -1;
  opacity: 0;
`;
export const QuestionCard = styled.div`
  position: relative;
  background: ${themeColor('mono7')};
  border-radius: 32px;
  padding: ${themeSpace(6)} ${themeSpace(6)} 0;
  ${themeMediaQuery('sm')} {
    padding: ${themeSpace(9)} ${themeSpace(9)} 0;
  }
  height: 100%;
  width: 100%;
`;

export const QuestionCardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  width: 100%;
  &.fade-in-enter {
    opacity: 0;
  }
  &.fade-in-enter-active {
    opacity: 1;
    transition: ${animationTransition('opacity')};
  }
  &.fade-in-exit {
    opacity: 1;
  }
  &.fade-in-exit-active {
    opacity: 0;
    transition: ${animationTransition('opacity')};
  }
`;
export const QuestionCardTag = styled(CloudImage)<{ $isHidden?: boolean }>`
  height: 32px;
  ${themeMediaQuery('sm')} {
    height: 48px;
  }
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`;
export const QuestionCardContent = styled(Text)`
  margin-top: ${themeSpace(8)};
`;

export const PrivateQuestionFooter = styled.div`
  position: absolute;
  width: 100%;
  height: 30px;
  bottom: 16px;
  ${themeMediaQuery('sm')} {
    height: 50px;
    bottom: 37px;
  }

  left: 0;
`;

export const PrivateQuestionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${themeSpace(1)};
  ${themeMediaQuery('sm')} {
    margin-bottom: ${themeSpace(7)};
  }
  transform: translateY(25px);
  opacity: 0;
`;
export const PrivateQuestionLabelIcon = styled(CloudImage)`
  width: 18px;
  ${themeMediaQuery('sm')} {
    width: 22px;
  }
`;

export const PrivateQuestionAvatarsDiv = styled.div`
  display: flex;
  justify-content: center;
  padding-left: ${themeSpace(4)};
`;

export const PrivateQuestionAvatar = styled(Avatar).attrs({
  sizeVariant: ['32px', '56px'],
  strokeWidth: '2px',
  strokeColor: 'green1',
  className: 'private-question-avatar',
})`
  display: flex;
  justify-content: center;
  margin-left: ${themeSpace(-4)};
  transform: scale(0.5);
  opacity: 0;
`;

export const QuestionCardSpacer = styled.div`
  margin: ${themeSpace(10)} 0;
  height: 550px;
`;
export const LottieContainer = styled.div``;
export const LottieContainerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin-top: ${themeSpace(6)};
  padding: ${themeSpace(8)} ${themeSpace(6)} 0;
  ${themeMediaQuery('sm')} {
    margin-top: ${themeSpace(7)};
    padding: ${themeSpace(11)} ${themeSpace(9)} 0;
  }
  transition: ${animationTransitionExt({ property: 'opacity', delay: STANDARD_DURATION })};
  &.transparent {
    opacity: 0;
    transition: ${animationTransitionExt({ property: 'opacity' })};
  }
`;

export const QuestionSuggestionsColumns = styled(Columns)`
  padding-top: ${themeSpace(8)};
  ${themeMediaQuery('sm')} {
    padding-top: ${themeSpace(13)};
  }
`;

export const DetailedTextPairing = styled(TextPairing)`
  .text-pairing-title {
    max-width: 454px;
    ${themeMediaQuery('xl')} {
      max-width: 520px;
    }
  }
`;

export const QuestionSuggestionTextPairing = styled(TextPairing)`
  .text-pairing-title {
    max-width: 355px;
    ${themeMediaQuery('xl')} {
      max-width: 400px;
    }
  }
`;

export const SecondSectionTextPairing = styled(TextPairing)`
  .text-pairing-title {
    max-width: 434px;
    ${themeMediaQuery('xl')} {
      max-width: 470px;
    }
  }
`;
export const SecondSectionDetailTextPairing = styled(TextPairing)`
  ${themeMediaQuery('below_sm')} {
    margin-top: ${themeSpace(4)};
  }
  .text-pairing-title {
    max-width: 400px;
    ${themeMediaQuery('xl')} {
      max-width: 470px;
    }
  }
`;

export const SecondSectionBottomDetailTextPairing = styled(TextPairing)`
  .text-pairing-title {
    max-width: 369px;
    ${themeMediaQuery('xl')} {
      max-width: 380px;
    }
  }
`;

export const PhotoCreditContent = styled(PhotoCredit)`
  transform: rotate(-90deg);
  -webkit-transform: rotate(-90deg);
  -moz-transform: rotate(-90deg);
  -ms-transform: rotate(-90deg);
  -o-transform: rotate(-90deg);
  margin-right: -${themeSpace(11)};
  margin-top: ${themeSpace(7)};
`;
